import {
  usePlayers,
  useRoundArtwork,
  useSetSharingPlayer,
  useSharedPlayers,
  useSharingPlayer
} from '@/hooks/HostSessionControlHooks';
import * as d3 from 'd3';
import Konva from 'konva';
import { Stage } from 'konva/lib/Stage';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Group, Stage as KStage, Layer, Rect, Text } from 'react-konva';
import { Artwork } from '@/API';
import { SubmittedDrawPlayerImage } from '@/utils/submittedDrawing';

// Constants
const DEFAULT_SHARING_CARD_SIZE = { width: 364, height: 520 };
const SMALL_SHARING_CARD_SIZE = { width: 287, height: 410 };
export const DEFAULT_CARD_SIZE = { width: 175, height: 250 };
export const COMPACT_CARD_SIZE = { width: 119, height: 170 };
export const DEFAULT_CORNER_RADIUS = 60 / 364;
export const DEFAULT_BORDER_SIZE = 2 / 364;
export const DEFAULT_DRAWING_SCALE = 315 / 364;
export const DEFAULT_TOP_MARGIN_SIZE = 15 / 520;
export const DEFAULT_FONT_SIZE = 25 / 364;

// Types
type Node = {
  id: string;
  card: Artwork;
  x: number;
  y: number;
  vx: number;
  vy: number;
  fx?: number | null;
  fy?: number | null;
  rotation: number;
};

type Link = {
  source: string;
  target: string;
};

type GraphData = {
  nodes: Node[];
  links: Link[];
};

/**
 * PlayersCards component displays a network of player cards with interactive features.
 */
export default function PlayerCards() {
  // State
  const [graphData, setGraphData] = useState<GraphData>({ nodes: [], links: [] });
  const [selectedNode, setSelectedNode] = useState<Node | null>(null);
  const [alreadySelectedNodes, setAlreadySelectedNodes] = useState<Node[]>([]);
  const [initialized, setInitialized] = useState(false);
  const [gameContainerDimensions, setGameContainerDimensions] = useState({
    width: 0,
    height: 0
  });

  // Refs
  const stageRef = useRef<Stage | null>(null);
  const simulationRef = useRef<d3.Simulation<Node, undefined> | null>(null);
  const selectedSimulationRef = useRef<d3.Simulation<Node, undefined> | null>(null);
  const nodeRefs = useRef<{ [key: string]: Konva.Group }>({});
  const initializedNodes = useRef<Set<string>>(new Set());
  const pendingInitializations = useRef<Set<string>>(new Set());
  const [dragging, setDragging] = useState(false);

  // Hooks
  const players = usePlayers();
  const playerCards = useRoundArtwork();
  const setSharingPlayer = useSetSharingPlayer();
  const sharingPlayer = useSharingPlayer();
  const sharedPlayers = useSharedPlayers();

  const smallScreen = window.innerHeight < 849 && window.innerWidth < 1313;

  // Dynamic card size based on number of cards
  const showSmallCards = smallScreen || playerCards.length > 10;
  const CARD_SIZE = showSmallCards ? COMPACT_CARD_SIZE : DEFAULT_CARD_SIZE;
  const SHARING_CARD_SIZE = smallScreen ? SMALL_SHARING_CARD_SIZE : DEFAULT_SHARING_CARD_SIZE;

  const LINK_MIN_LENGTH = CARD_SIZE.width;

  /**
   * Computes and sets the game area dimensions based on window size.
   */
  const computeGameArea = useCallback(() => {
    setGameContainerDimensions({
      // Note: Buffer on each side of the game area.
      width: Math.round(window.innerWidth * 0.78 - 20),

      // Note: Subtracting 100 to account for the bottom navbar.
      height: Math.round(window.innerHeight - 100)
    });
  }, []);

  useEffect(() => {
    computeGameArea();
    window.addEventListener('resize', computeGameArea);
    return () => window.removeEventListener('resize', computeGameArea);
  }, [computeGameArea]);

  useEffect(() => {
    setAlreadySelectedNodes(
      graphData.nodes.filter((node) =>
        sharedPlayers.some((sharedPlayer) => sharedPlayer?.id === node.card.playerId)
      )
    );

    const sharingNode = sharingPlayer
      ? (graphData.nodes.find((node) => node.card.playerId === sharingPlayer?.id) ?? null)
      : null;

    setSelectedNode(sharingNode);
    if (graphData.nodes.length > 0) {
      setInitialized(true);
    }
  }, [players, graphData.nodes]);

  useEffect(() => {
    setDragging(false);
  }, [sharingPlayer?.id]);

  useEffect(() => {
    if (gameContainerDimensions.width === 0) {
      return;
    }

    if (!playerCards?.length) {
      setSelectedNode(null);
      setAlreadySelectedNodes([]);
    }

    const availableSpace = {
      width: gameContainerDimensions.width - SHARING_CARD_SIZE.width,
      height: gameContainerDimensions.height - SHARING_CARD_SIZE.height
    };

    const allNodes: Node[] = playerCards
      .filter((card) => !!card)
      .map((card) => {
        const existingNode = graphData.nodes.find((node) => node.id === card.id);

        return {
          id: card.id,
          card,
          x: existingNode
            ? existingNode.x
            : Math.random() * availableSpace.width + SHARING_CARD_SIZE.width / 2,
          y: existingNode
            ? existingNode.y
            : Math.random() * availableSpace.height + SHARING_CARD_SIZE.height / 2,
          rotation: existingNode
            ? existingNode.rotation
            : Math.ceil(Math.random() * 10) * (Math.round(Math.random()) ? 1 : -1),
          vx: 0,
          vy: 0
        };
      });

    // Track which nodes need initial scale setup
    const newNodeIds = allNodes
      .filter((node) => !initializedNodes.current.has(node.id))
      .map((node) => node.id);

    newNodeIds.forEach((id) => pendingInitializations.current.add(id));

    // Always update state immediately so new cards appear right away
    setGraphData((prevData) => ({ ...prevData, nodes: [...allNodes] }));

    if (!initialized) {
      return;
    }

    const nodes = allNodes.filter((node) => node.id !== selectedNode?.id);
    const highlightedNode = allNodes.filter((node) => node.id === selectedNode?.id);

    const links: Link[] = nodes.slice(0, -1).map((node, i) => ({
      source: node.id,
      target: nodes[i + 1].id
    }));

    const containerSpace = gameContainerDimensions.width * gameContainerDimensions.height;
    const cardsSpace = CARD_SIZE.width * CARD_SIZE.height * 1 * nodes.length;
    const spacing = (containerSpace - cardsSpace) / nodes.length;

    // Set up D3 force simulation
    const simulation = d3
      .forceSimulation(nodes)

      // Keep nodes from overlapping - adjust based on number of cards
      // Fewer cards = larger radius and stronger force (no overlap)
      // More cards = smaller radius and weaker force (allow tighter packing)
      .force(
        'collide',
        d3.forceCollide(Math.min(Math.max(0, spacing), CARD_SIZE.width * 0.75)).strength(0.05)
      )

      // Bounding box
      .force('bound', () => {
        for (const node of nodes) {
          const nodeX = node.x - CARD_SIZE.width / 2;
          const nodeY = node.y - CARD_SIZE.height / 2;

          if (nodeX < 25) {
            node.x = 25 + CARD_SIZE.width / 2;
          } else if (nodeX + CARD_SIZE.width > gameContainerDimensions.width) {
            node.x = gameContainerDimensions.width - CARD_SIZE.width - 15 + CARD_SIZE.width / 2;
          }

          if (nodeY < 20) {
            node.y = 20 + CARD_SIZE.height / 2;
          } else if (nodeY + CARD_SIZE.height > gameContainerDimensions.height) {
            node.y = gameContainerDimensions.height - CARD_SIZE.height - 15 + CARD_SIZE.height / 2;
          }
        }
      })

      // Forces the nodes to gravitate towards each other
      .force(
        'link',
        d3
          .forceLink(links)
          .id((d: any) => d.id)
          .distance(LINK_MIN_LENGTH)
          .strength(Math.max(0.001, 0.05 / nodes.length))
      )

      .force(
        'center',
        d3
          .forceCenter(gameContainerDimensions.width / 2, gameContainerDimensions.height / 2)
          .strength(0.01)
      )

      .on('tick', () => {
        // Update node positions directly without triggering state updates
        for (const node of nodes) {
          const group = nodeRefs.current[node.id];
          if (group) {
            group.x(node.x);
            group.y(node.y);
          }
        }
        stageRef.current?.batchDraw();
      });

    // Set up D3 force simulation
    const selectedSimulation = d3
      .forceSimulation(highlightedNode)

      // Bounding box
      .force('bound', () => {
        for (const node of highlightedNode) {
          const nodeX = node.x - DEFAULT_CARD_SIZE.width;
          const nodeY = node.y - DEFAULT_CARD_SIZE.height;

          if (nodeX < 25) {
            node.x = 25 + DEFAULT_CARD_SIZE.width;
          } else if (nodeX + SHARING_CARD_SIZE.width > gameContainerDimensions.width) {
            node.x =
              gameContainerDimensions.width -
              SHARING_CARD_SIZE.width -
              15 +
              DEFAULT_CARD_SIZE.width;
          }

          if (nodeY < 20) {
            node.y = 20 + DEFAULT_CARD_SIZE.height;
          } else if (nodeY + SHARING_CARD_SIZE.height > gameContainerDimensions.height) {
            node.y =
              gameContainerDimensions.height -
              SHARING_CARD_SIZE.height -
              15 +
              DEFAULT_CARD_SIZE.height;
          }
        }
      })

      .force('center', () => {
        // Only apply center force to selected node if it's not being dragged
        for (const node of highlightedNode) {
          if (!dragging) {
            const centerX = gameContainerDimensions.width / 2;
            const centerY = gameContainerDimensions.height / 2;
            node.vx = (node.vx || 0) + (centerX - node.x) * 0.01;
            node.vy = (node.vy || 0) + (centerY - node.y) * 0.01;
          }
        }
      })

      .on('tick', () => {
        // Update node positions directly without triggering state updates
        for (const node of highlightedNode) {
          const group = nodeRefs.current[node.id];
          if (group) {
            group.x(node.x);
            group.y(node.y);
          }
        }
        stageRef.current?.batchDraw();
      });

    simulationRef.current = simulation;
    selectedSimulationRef.current = selectedSimulation;

    return () => {
      simulation.stop();
      selectedSimulation.stop();
    };
  }, [playerCards, gameContainerDimensions, initialized, selectedNode?.id, dragging]);

  /**
   * Handles node click event, setting the sharing player and animating the node.
   */
  const handleNodeClick = useCallback(
    (node: Node) => {
      // Check if the player has not been selected before.
      if (alreadySelectedNodes.includes(node)) return;

      setSharingPlayer(node.card.playerId);
    },
    [setSharingPlayer, selectedNode]
  );

  /**
   * Changes the cursor style when entering or leaving a node.
   */
  const handleMouseEnterLeave = (action: 'enter' | 'leave', node: Node) => {
    if (stageRef.current) {
      if (alreadySelectedNodes.includes(node)) {
        stageRef.current.container().style.cursor = 'not-allowed';
        return;
      }

      if (action === 'enter') {
        stageRef.current.container().style.cursor = 'pointer';
        return;
      }

      stageRef.current.container().style.cursor = 'default';
    }
  };

  /**
   * Renders a single node (player card) in the graph.
   */
  const renderNode = useCallback(
    (node: Node) => {
      const drawing = JSON.parse(node.card.drawing ?? '{}');
      const isSelected = node === selectedNode;
      const isAlreadySelected = alreadySelectedNodes.includes(node);
      const nodeCardSize = isSelected ? DEFAULT_CARD_SIZE : CARD_SIZE;
      const scaleX = SHARING_CARD_SIZE.width / nodeCardSize.width;
      const scaleY = SHARING_CARD_SIZE.height / nodeCardSize.height;

      return (
        <Group
          key={node.id}
          ref={(ref) => {
            if (!initialized) return;

            if (ref && gameContainerDimensions.width > 0) {
              nodeRefs.current[node.id] = ref;

              const isNewNode = !initializedNodes.current.has(node.id);

              ref.offsetX(nodeCardSize.width / 2); // Move the offset to the centre of the shape
              ref.offsetY(nodeCardSize.height / 2);

              if (isNewNode) {
                // Initial appearance: set scale to 0 immediately, then animate
                initializedNodes.current.add(node.id);

                ref.scale({ x: 0, y: 0 });

                // Animate to target scale
                ref.to({
                  scaleX: isSelected ? scaleX : 1,
                  scaleY: isSelected ? scaleY : 1,
                  duration: 0.5,
                  easing: Konva.Easings.EaseInOut,
                  onFinish: () => {
                    // Remove from pending and restart simulation when all nodes are initialized
                    pendingInitializations.current.delete(node.id);
                    if (pendingInitializations.current.size === 0 && simulationRef.current) {
                      simulationRef.current.alpha(1).restart();
                    }
                  }
                });
              } else {
                if (pendingInitializations.current.has(node.id)) return;
                // Animate scale transition for selection changes
                ref.to({
                  scaleX: isSelected ? SHARING_CARD_SIZE.width / nodeCardSize.width : 1,
                  scaleY: isSelected ? SHARING_CARD_SIZE.height / nodeCardSize.height : 1,
                  duration: 0.5,
                  easing: Konva.Easings.EaseInOut
                });
              }
            }
          }}
          x={node.x}
          y={node.y}
          draggable
          onMouseEnter={() => handleMouseEnterLeave('enter', node)}
          onMouseLeave={() => handleMouseEnterLeave('leave', node)}
          onDragStart={() => {
            setDragging(true);
          }}
          onDragMove={(e) => {
            const isSelected = node.id === selectedNode?.id;

            const newX = e.target.x();
            const newY = e.target.y();
            node.x = newX;
            node.y = newY;
            node.fx = newX;
            node.fy = newY;
            if (isSelected) {
              selectedSimulationRef.current?.alpha(1).restart();
            } else {
              simulationRef.current?.alpha(1).restart();
            }
          }}
          onDragEnd={() => {
            node.fx = null;
            node.fy = null;
          }}
          onClick={() => handleNodeClick(node)}
          zIndex={isSelected ? 100 : 0}
          opacity={initializedNodes.current.has(node.id) ? 1 : 0}
          rotation={isSelected ? 0 : node.rotation}
        >
          <Rect
            width={nodeCardSize.width}
            height={nodeCardSize.height}
            opacity={100}
            stroke={isSelected ? '#D51668' : '#d3d3d3'}
            strokeWidth={2}
            cornerRadius={DEFAULT_CORNER_RADIUS * nodeCardSize.width}
            fill={isAlreadySelected ? '#f3f3f3' : '#ffffff'}
            shadowColor="#000000"
            shadowOpacity={0.15}
            shadowEnabled={true}
            shadowOffsetX={0}
            shadowOffsetY={9}
            shadowBlur={24}
          />
          <SubmittedDrawPlayerImage drawing={drawing} cardSize={nodeCardSize} />

          {(() => {
            const playerName =
              players?.find((player) => player?.id === node.card.playerId)?.name ?? '';
            const fontSize = Math.ceil(DEFAULT_FONT_SIZE * nodeCardSize.width);
            const text = playerName.length > 18 ? `${playerName.slice(0, 14)}...` : playerName;
            const margin = DEFAULT_TOP_MARGIN_SIZE * nodeCardSize.width;

            return (
              <>
                <Text
                  text={text}
                  fontSize={fontSize}
                  fill={'#20145F'}
                  width={nodeCardSize.width}
                  offsetY={-nodeCardSize.height + fontSize + margin + 2}
                  align="center"
                  verticalAlign="middle"
                  height={fontSize}
                />
              </>
            );
          })()}
        </Group>
      );
    },
    [selectedNode, alreadySelectedNodes, handleNodeClick, players]
  );

  return (
    <div className="fixed top-0 left-25% ml-20">
      <KStage
        ref={stageRef}
        width={gameContainerDimensions.width}
        height={gameContainerDimensions.height}
      >
        <Layer>{graphData.nodes.map(renderNode)}</Layer>
      </KStage>
    </div>
  );
}
